/**
 * impact_lights — the short-lived lights that make a blaster hit land ON the
 * world instead of in front of it.
 *
 * WHY THIS EXISTS. Bloom spreads bright PIXELS; it does not light anything. A
 * white-hot impact quad with no light beside it leaves the wall it struck
 * exactly as dark as it was — the effect reads as a sticker. One finite-range
 * point light for a few dozen milliseconds is what makes the crate, the ground
 * and the shooter's own hands flash.
 *
 * WHY A POOL. Lights are the expensive part: each one is a shader permutation's
 * worth of work on every receiver in range, and a firefight can ask for dozens
 * per second. So there is a fixed pool, and candidates compete for it.
 *
 * ONE POOL FOR EVERY LOCAL LIGHT (fire realism R2, 2026-09-25). When the fire
 * module has attached the scene's shared `LightBroker` (lib/vfx/light_broker),
 * impacts take their slots FROM it — `capacity` becomes the impact QUOTA, and
 * fires, fireworks and explosions compete for the same session-fixed lights
 * by the same importance. With no shared broker (a bare scene, the harness)
 * the pool owns a private broker of exactly `capacity` lights — the original
 * behaviour, bit for bit.
 *
 * RANKING AND HYSTERESIS. Candidates are ranked by importance — brightness
 * falling off with distance to the viewer — and an incumbent light is only
 * evicted by a candidate meaningfully better than it (`EVICT_MARGIN`).
 * Without that margin two similar impacts swap the same slot every frame and
 * the scene strobes. A light that is merely dimmer than a newcomer keeps its
 * slot until it dies on its own; it is already short-lived.
 *
 * THE LIGHT-SET IDENTITY LAW. Pool membership is FIXED for the session and
 * every light stays PERMANENTLY visible. Flipping a light's `visible` changes
 * the collected light set, which changes the lights-node cache key, which
 * invalidates every lit material's pipeline — and the next render rebuilds the
 * whole scene's TSL graphs synchronously. StreetLampsModule live-traced that at
 * 7–12 s on real hardware in July 2026; doing it per impact made firing the gun
 * hang the game (2026-09-15). Idle slots are intensity 0 and PARKED far below
 * the world, so a zero-intensity light cannot reach anything. Only uniforms
 * ever change.
 *
 * The pool is created once and never disposed per shot (the GPU shield law:
 * nothing ever drawn is destroyed).
 */
import type * as THREE from 'three/webgpu';
import { LightBroker } from './vfx/light_broker.js';

export interface ImpactLightRequest {
  x: number; y: number; z: number;
  color: number;
  /** Peak intensity, decayed to 0 over `ms`. */
  intensity: number;
  rangeM: number;
  ms: number;
}

export class ImpactLightPool {
  private readonly broker: LightBroker;
  /** True when this pool owns a private broker (no shared one on the scene). */
  private readonly own: boolean;
  private readonly quota: number;

  /**
   * `capacity` is the whole budget for impact lighting. 4–8 is a sane desktop
   * starting point; the cheap tiers should pass 2.
   */
  constructor(scene: THREE.Scene, capacity: number) {
    this.quota = Math.max(0, capacity);
    const shared = LightBroker.of(scene);
    if (shared) {
      this.broker = shared;
      this.own = false;
      shared.setQuota('impact', this.quota);
    } else {
      this.broker = new LightBroker(scene, this.quota, 'impact-lights');
      this.own = true;
    }
  }

  get capacity(): number { return this.own ? this.broker.capacity : Math.min(this.quota, this.broker.capacity); }
  get active(): number { return this.broker.usedBy('impact'); }
  get stats(): { taken: number; refused: number; evicted: number } { return this.broker.stats.impact; }
  /** True when impacts ride the scene's shared broker (fires, fireworks and explosions in the same pool). */
  get shared(): boolean { return !this.own; }

  /**
   * Ask for a light. Returns true if one was allocated.
   *
   * RANGE IS CLAMPED, deliberately: these lights are unshadowed, so a large
   * radius leaks through a thin wall and lights the room next door. Keeping the
   * radius small bounds how far a leak can reach, which is cheaper and more
   * predictable than trying to shadow a 50 ms flash.
   */
  request(req: ImpactLightRequest, viewer: { x: number; y: number; z: number }): boolean {
    return this.broker.flash({ ...req, cat: 'impact', maxRangeM: 12 }, viewer);
  }

  /**
   * Age every light. `dtMs` is wall time — these are ambient flashes and must
   * not stretch when the simulation hitches. A shared broker is aged ONCE per
   * frame by the fire module; aging it here too would double its clock.
   */
  update(dtMs: number): void {
    if (this.own) this.broker.update(dtMs);
  }

  dispose(): void {
    if (this.own) this.broker.dispose();
    else this.broker.setQuota('impact', 0);
  }

  /** Diagnostic: every slot's visibility must be true, forever. */
  get membershipIntact(): boolean { return this.broker.membershipIntact; }
}
